import React, { useMemo } from "react";
import {
  useTable,
  Column,
  useFilters,
  UseFiltersInstanceProps,
  TableInstance,
  useSortBy,
} from "react-table";
import {
  FundsTransferAccountFragment,
  DistributorFundsTransferFieldsFragment,
} from "@apollo/ops";
import { Input } from "@components/ui/Input";
import { Table } from "@components/ui/Table";
import { formatAmountInCents } from "@utilities/currency";
import { formatAsDate } from "@utilities/formatDate";

type DistributorTransactionsTableProps = {
  fundsTransfers: DistributorFundsTransferFieldsFragment[];
};

export function DistributorTransactionsTable({
  fundsTransfers,
}: DistributorTransactionsTableProps) {
  const columns = useMemo<Column<DistributorFundsTransferFieldsFragment>[]>(
    () => [
      {
        Header: "Date Initiated",
        Cell: ({ value }) => formatAsDate(new Date(value)),
        accessor: "createdAt",
      },
      {
        Header: (
          <>
            Brewery <span className="whitespace-nowrap">Invoice #</span>
          </>
        ),
        accessor: "breweryInvoiceNumber",
      },
      {
        Header: (
          <>
            Retailer <span className="whitespace-nowrap">Invoice #</span>
          </>
        ),
        accessor: "retailerInvoiceNumber",
      },
      {
        Header: "Amount",
        Cell: ({ value }) => formatAmountInCents(value),
        accessor: "amount",
      },
      {
        Header: "From",
        Cell: ToCell,
        accessor: "source",
      },
      {
        Header: "To",
        Cell: ToCell,
        accessor: "destination",
      },
      {
        Header: "Statement Descriptor",
        accessor: "statementDescriptor",
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: fundsTransfers,
    },
    useFilters,
    useSortBy
  ) as TableInstance<DistributorFundsTransferFieldsFragment> &
    UseFiltersInstanceProps<DistributorFundsTransferFieldsFragment>;

  const { setFilter } = tableInstance;

  return (
    <>
      <div className="flex mb-1">
        <div className="mr-8 w-48">
          <Input
            type="text"
            onChange={(e) => setFilter("breweryInvoiceNumber", e.target.value)}
            placeholder="Search Brewery invoices"
          />
        </div>

        <div className="w-48">
          <Input
            type="text"
            onChange={(e) => setFilter("retailerInvoiceNumber", e.target.value)}
            placeholder="Search Retailer invoices"
          />
        </div>
      </div>

      <Table instance={tableInstance} />
    </>
  );
}

type ToCellProps = {
  value: FundsTransferAccountFragment;
};

function ToCell({ value: fundsAccount }: ToCellProps) {
  return (
    <>
      <div>{fundsAccount.business.displayName}</div>
      <div>{fundsAccount.label}</div>
    </>
  );
}
